/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Copy, Download, Share2, QrCode, MessageCircle, Printer } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Phase 323 — Self-Signup QR
 * --------------------------
 * Opens a dialog with the wa.me deep-link QR for parents/coaches to scan.
 * Scanning opens WhatsApp pre-filled with "Start Registration" (student)
 * or "Start Employee Registration" (employee) — triggering our existing
 * Phase 302 onboarding bot.
 *
 * Props:
 *   open        — controlled flag
 *   onClose     — fn to close
 *   role        — 'student' | 'employee'
 */
export default function SelfSignupQRDialog({ open, onClose, role = 'student' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/academy/self-signup-qr?role=${role}`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load self-signup QR');
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleCopyLink = () => {
    if (!data?.wa_link) return;
    navigator.clipboard.writeText(data.wa_link);
    toast.success('WhatsApp link copied!');
  };

  const handleDownload = () => {
    if (!data?.qr_data_url) return;
    const a = document.createElement('a');
    a.href = data.qr_data_url;
    a.download = role === 'employee' ? 'employee-signup-qr.png' : 'student-signup-qr.png';
    a.click();
  };

  const handleShare = () => {
    if (!data?.wa_link) return;
    const msg = role === 'employee'
      ? `Join our academy as a coach/staff member — tap to open WhatsApp:\n${data.wa_link}`
      : `Register your child with our academy — tap to open WhatsApp:\n${data.wa_link}`;
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(shareUrl, '_blank', 'noopener');
  };

  const handleDownloadPoster = async () => {
    try {
      const res = await api.get(`/academy/self-signup-qr.pdf?role=${role}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = role === 'employee' ? 'employee-signup-poster.pdf' : 'student-signup-poster.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('A4 poster downloaded — print and stick at the gate!');
    } catch (err) {
      toast.error('Failed to generate poster PDF');
    }
  };

  const label = role === 'employee' ? 'Employee Self-Signup QR' : 'Student Self-Signup QR';
  const subtitle = role === 'employee'
    ? 'Print this or drop it in your staff WhatsApp group. Anyone who scans gets WhatsApp pre-filled with "Start Employee Registration" — our bot walks them through joining.'
    : 'Print this or drop it in your parents WhatsApp group. Anyone who scans gets WhatsApp pre-filled with "Start Registration" — our bot collects child name, DOB, sport, and emergency contact, then creates the student record automatically.';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="self-signup-qr-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-emerald-600" />
            {label}
          </DialogTitle>
          <DialogDescription className="text-xs">{subtitle}</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="space-y-3">
            <Skeleton className="h-64 w-64 mx-auto rounded-lg" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {!loading && data && !data.configured && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="qr-not-configured">
            <p className="font-semibold text-amber-900">WhatsApp business number not configured</p>
            <p className="text-xs text-amber-700 mt-1">
              Ask your super-admin to set the academy WhatsApp display phone before issuing a signup QR.
            </p>
          </div>
        )}

        {!loading && data?.configured && (
          <div className="space-y-3">
            <div className="flex justify-center bg-white p-4 rounded-lg border border-slate-200">
              <img
                src={data.qr_data_url}
                alt={`${label} QR code`}
                className="w-64 h-64"
                data-testid="self-signup-qr-image"
              />
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs">
              <p className="font-semibold text-slate-700 mb-0.5 flex items-center gap-1">
                <MessageCircle className="h-3 w-3 text-emerald-600" />
                Pre-filled message
              </p>
              <p className="font-mono text-slate-600 truncate" data-testid="self-signup-prefill">"{data.prefill_text}"</p>
              <p className="text-[10px] text-slate-500 mt-1">
                Sends to: <span className="font-mono">+{data.display_phone}</span>
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                data-testid="self-signup-copy-link"
                className="text-xs"
              >
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                data-testid="self-signup-download-qr"
                className="text-xs"
              >
                <Download className="h-3.5 w-3.5 mr-1" /> Download
              </Button>
              <Button
                size="sm"
                onClick={handleShare}
                data-testid="self-signup-share-wa"
                className="text-xs bg-emerald-600 hover:bg-emerald-700"
              >
                <Share2 className="h-3.5 w-3.5 mr-1" /> Share
              </Button>
            </div>
            <Button
              onClick={handleDownloadPoster}
              data-testid="self-signup-download-poster"
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
              size="sm"
            >
              <Printer className="h-4 w-4 mr-2" /> Download Printable A4 Poster
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
